import { t } from "./i18n";
import { keycloak } from "./keycloak";

/** Thrown for any non-2xx response (or an unreachable server: status 0). */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * The human-readable message inside a FastAPI error body.
 *
 * `detail` is a string for the domain errors (`{"detail": "projet clos : plus d'édition
 * possible"}`) and a list of `{msg, loc}` for request-validation errors; anything else (a
 * proxy's HTML page, an empty body) falls back to the HTTP status line.
 */
export function errorMessage(body: string, status: number, statusText = ""): string {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    const detail = parsed?.detail;
    if (typeof detail === "string" && detail.trim()) return capitalize(detail.trim());
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => validationMessage(d))
        .filter((m): m is string => m !== null);
      if (msgs.length) return msgs.join(" · ");
    }
  } catch {
    // Not JSON: fall through to the status line.
  }
  if (status === 0) return t("Server unreachable: check your connection.");
  const line = `${status}${statusText ? ` ${statusText}` : ""}`;
  return t("Error {{line}}", { line }).trim();
}

function validationMessage(item: unknown): string | null {
  if (!item || typeof item !== "object") return null;
  const { msg, loc } = item as { msg?: unknown; loc?: unknown };
  if (typeof msg !== "string" || !msg) return null;
  // `loc` is ["body", "field", ...]: name the field, it is what the user can act on.
  const field = Array.isArray(loc)
    ? loc.filter((p) => typeof p === "string" && p !== "body" && p !== "query").join(".")
    : "";
  return field ? `${field} : ${msg}` : msg;
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase("fr-FR") + text.slice(1);
}

/** 403 for an account that is not linked to any Boond person (see `ada/security.py`). */
export function isNotLinkedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && /non reli/i.test(error.message);
}

export type QueryValue = string | number | boolean | null | undefined;

/** `path?a=1&b=x`, skipping empty values (undefined, null, ""). */
export function withQuery(path: string, params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

export interface ApiRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON-serialised as the request body. */
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

export interface ApiClientDeps {
  /** A fresh bearer token, or throws when the session cannot be refreshed. */
  getToken: () => Promise<string | undefined>;
  onSessionExpired: () => void;
  fetchImpl?: typeof fetch;
}

export function createApi({ getToken, onSessionExpired, fetchImpl }: ApiClientDeps) {
  return async function api<T>(path: string, request: ApiRequest = {}): Promise<T> {
    let token: string | undefined;
    try {
      token = await getToken();
    } catch {
      onSessionExpired();
      throw new ApiError(401, t("Session expired: signing in again."));
    }
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (request.body !== undefined) headers["Content-Type"] = "application/json";

    let res: Response;
    try {
      res = await (fetchImpl ?? fetch)(withQuery(path, request.query), {
        method: request.method ?? (request.body !== undefined ? "POST" : "GET"),
        headers,
        body: request.body !== undefined ? JSON.stringify(request.body) : undefined,
        signal: request.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiError(0, errorMessage("", 0));
    }

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      if (res.status === 401) onSessionExpired();
      throw new ApiError(res.status, errorMessage(body, res.status, res.statusText));
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  };
}

/**
 * `fetch` wrapper for the same-origin API: refreshes the Keycloak token when it expires within
 * 30 s, adds the bearer header, parses JSON and turns FastAPI errors into `ApiError`.
 */
export const api = createApi({
  getToken: async () => {
    await keycloak.updateToken(30);
    return keycloak.token;
  },
  onSessionExpired: () => {
    void keycloak.login();
  },
});

/** Downloads a protected file (bearer token) and hands it to the browser under `filename`. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  await keycloak.updateToken(30);
  const res = await fetch(path, { headers: { Authorization: `Bearer ${keycloak.token ?? ""}` } });
  if (!res.ok) throw new ApiError(res.status, errorMessage(await res.text().catch(() => ""), res.status, res.statusText));
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
